import { MAX_PAGE_SIZE } from '@vv/common';
import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { apiClient } from '../api/client';
import { useApi } from '../api/useApi';
import { AsyncBoundary } from '../components/AsyncState';
import { FairFlagButton } from '../components/FairFlagButton';
import { CloseIcon, FilterIcon, SearchIcon } from '../components/icons';
import { JobCard } from '../components/JobCard';
import {
  EMPTY_JOB_FILTERS,
  JobFiltersModal,
  type JobFilters,
} from '../components/search/JobFiltersModal';
import {
  filtersFromUrl,
  filtersToUrl,
  readSearchUrl,
  writeSearchUrl,
  type SearchUrlState,
} from '../components/search/search-url';
import { StandLogo } from '../components/StandLogo';
import { FROM_SEARCH, paths } from '../routes';
import { accentFor, accentText } from '../utils/pavilion-accent';

interface ActiveChip {
  key: keyof JobFilters;
  id: string;
  label: string;
}

export function SearchPage(): ReactElement {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);

  // The URL is the page's state, so a search can be bookmarked and shared; see
  // search-url.ts. Each change replaces the history entry rather than adding
  // one, so Back leaves the search instead of undoing it a checkbox at a time.
  const urlState = useMemo(() => readSearchUrl(searchParams), [searchParams]);
  const { tab, q: query } = urlState;

  const update = (changes: Partial<SearchUrlState>): void => {
    setSearchParams(writeSearchUrl({ ...urlState, ...changes }), { replace: true });
  };

  const available = useApi(() => apiClient.getOfferFilters());

  // The URL names filters; the API takes ids, which need the fair's options.
  const filters = useMemo(
    () =>
      available.data === undefined ? EMPTY_JOB_FILTERS : filtersFromUrl(urlState, available.data),
    [urlState, available.data],
  );
  // A filtered link waits for the options rather than first showing every job.
  const filtersPending =
    available.loading &&
    urlState.category.length +
      urlState.position.length +
      urlState.exhibitor.length +
      urlState.location.length >
      0;

  const setFilters = (next: JobFilters): void => {
    if (available.data !== undefined) {
      update(filtersToUrl(next, available.data));
    }
  };

  const needle = query.trim().toLowerCase();

  // Filtering happens server-side: GET /offers takes exactly these parameters,
  // so the page sends them rather than fetching everything and narrowing here.
  const offers = useApi(
    () =>
      filtersPending
        ? new Promise<never>(() => undefined)
        : apiClient.getOffers({
            limit: MAX_PAGE_SIZE,
            q: needle === '' ? undefined : needle,
            pavilionIds: filters.pavilionIds,
            categoryIds: filters.categoryIds,
            standIds: filters.standIds,
            locations: filters.locations,
          }),
    [needle, filters, filtersPending],
  );

  const exhibitorResults = useApi(
    () => apiClient.getStands({ limit: MAX_PAGE_SIZE, q: needle === '' ? undefined : needle }),
    [needle],
  );

  const jobs = offers.data?.items ?? [];
  const exhibitors = exhibitorResults.data?.items ?? [];

  const chips = useMemo((): ActiveChip[] => {
    const labelFor = (key: keyof JobFilters, id: string): string => {
      if (key === 'pavilionIds') {
        return available.data?.pavilions.find((entry) => String(entry.id) === id)?.name ?? id;
      }

      if (key === 'categoryIds') {
        return available.data?.categories.find((entry) => String(entry.id) === id)?.name ?? id;
      }

      if (key === 'standIds') {
        return available.data?.stands.find((entry) => String(entry.id) === id)?.title ?? id;
      }

      return id;
    };

    return (Object.keys(filters) as (keyof JobFilters)[]).flatMap((key) =>
      filters[key].map((value) => ({
        key,
        id: String(value),
        label: labelFor(key, String(value)),
      })),
    );
  }, [filters, available.data]);

  const removeChip = (chip: ActiveChip): void => {
    if (chip.key === 'locations') {
      setFilters({ ...filters, locations: filters.locations.filter((value) => value !== chip.id) });

      return;
    }

    const numeric = Number(chip.id);

    setFilters({ ...filters, [chip.key]: filters[chip.key].filter((value) => value !== numeric) });
  };

  return (
    <div className="app-shell pb-12">
      <h1 className="flex items-center gap-3 py-2 text-3xl font-bold text-fog-100">
        {t('search.title')}
        <FairFlagButton className="size-8" />
      </h1>

      <div
        role="tablist"
        aria-label={t('search.title')}
        className="mt-4 flex rounded-xl bg-ink-900 p-1"
      >
        {(['jobs', 'exhibitors'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => {
              update({ tab: key });
            }}
            className={`flex-1 rounded-lg py-2.5 text-center transition-colors ${
              tab === key ? 'bg-ink-800 font-bold text-fog-100' : 'text-fog-500'
            }`}
          >
            {t(`search.tabs.${key}`)}
          </button>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-fog-500" />
          <input
            value={query}
            onChange={(event) => {
              update({ q: event.target.value });
            }}
            placeholder={
              tab === 'jobs' ? t('search.placeholderJobs') : t('search.placeholderExhibitors')
            }
            aria-label={
              tab === 'jobs' ? t('search.placeholderJobs') : t('search.placeholderExhibitors')
            }
            className="w-full rounded-full border border-ink-700 bg-transparent py-3 pr-11 pl-12 text-fog-100 placeholder:text-fog-600 focus:ring-2 focus:ring-primary focus:outline-none"
          />
          {query !== '' && (
            <button
              type="button"
              onClick={() => {
                update({ q: '' });
              }}
              aria-label={t('common.close')}
              className="absolute top-1/2 right-3 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-ink-600 text-fog-300"
            >
              <CloseIcon className="size-4" />
            </button>
          )}
        </div>

        {/* Filters apply to job results only; the exhibitor tab has none. */}
        {tab === 'jobs' && (
          <button
            type="button"
            onClick={() => {
              setFiltersOpen(true);
            }}
            aria-label={t('search.openFilters')}
            className="shrink-0 text-fog-100 transition-opacity hover:opacity-70"
          >
            <FilterIcon className="size-7" />
          </button>
        )}
      </div>

      {tab === 'jobs' && chips.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {chips.map((chip) => (
            <li key={`${chip.key}-${chip.id}`}>
              <button
                type="button"
                onClick={() => {
                  removeChip(chip);
                }}
                className="flex items-center gap-1.5 rounded-md bg-ink-700 py-1 pr-2 pl-2.5 text-xs text-fog-100"
              >
                {chip.label}
                <CloseIcon className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {tab === 'jobs' ? (
        <section className="mt-6">
          <h2 className="mb-3 text-xl font-bold text-fog-100">{t('search.resultsJobs')}</h2>
          <AsyncBoundary loading={offers.loading} error={offers.error} onRetry={offers.reload}>
            {jobs.length === 0 ? (
              <p className="text-fog-500">{t('search.emptyJobs')}</p>
            ) : (
              <ul className="space-y-4">
                {jobs.map((job) => (
                  <li key={job.id}>
                    <JobCard
                      job={job}
                      showCompany
                      from={FROM_SEARCH}
                      state={{ search: searchParams.toString() }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </AsyncBoundary>
        </section>
      ) : (
        <section className="mt-6">
          <h2 className="mb-3 text-xl font-bold text-fog-100">{t('search.resultsExhibitors')}</h2>
          <AsyncBoundary
            loading={exhibitorResults.loading}
            error={exhibitorResults.error}
            onRetry={exhibitorResults.reload}
          >
            {exhibitors.length === 0 ? (
              <p className="text-fog-500">{t('search.emptyExhibitors')}</p>
            ) : (
              <ul className="space-y-4">
                {exhibitors.map((stand) => (
                  <li key={stand.id}>
                    <Link
                      to={paths.exhibitor(stand.slug)}
                      className="card relative block p-4 transition-colors hover:bg-ink-800"
                    >
                      {stand.chatEnabled && (
                        <span className="absolute top-4 right-4 rounded-full bg-white/10 px-3 py-1 text-xs text-online">
                          {t('stand.chatAvailable')}
                        </span>
                      )}
                      <StandLogo url={stand.logoUrl} className="size-14" />
                      <h3 className="mt-3 font-bold text-fog-100">{stand.title}</h3>
                      {/* A stand may exhibit in several pavilions, each in its colour. */}
                      <p className="mt-1 text-sm">
                        {stand.pavilions.map((pavilion, index) => (
                          <span key={pavilion.id}>
                            {index > 0 && <span className="text-fog-600"> · </span>}
                            <span className={accentText(accentFor(pavilion.position))}>
                              {pavilion.name}
                            </span>
                          </span>
                        ))}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </AsyncBoundary>
        </section>
      )}

      <JobFiltersModal
        open={filtersOpen}
        onClose={() => {
          setFiltersOpen(false);
        }}
        filters={filters}
        onChange={setFilters}
      />
    </div>
  );
}
